import { normalizeStorageKey } from './storageKey';

const TOP_LEVEL_DRUM_FIELDS = ['pattern', 'customSamples', 'noteData', 'drumVolumes', 'drumSounds', 'rowViewModes'];

/**
 * Whether a saved session has drum state to load. The studio keeps drums at
 * the top level; the AI plugin keeps them only in drum-lane pattern clips, and
 * those clips' samples load through the same drum loader.
 */
export function sessionHasDrumState(sessionState) {
  if (!sessionState || typeof sessionState !== 'object') return false;
  if (TOP_LEVEL_DRUM_FIELDS.some((field) => sessionState[field])) return true;
  return (sessionState.timelineClips || []).some((clip) => clip?.clipType === 'pattern'
    && clip.clipData?.kind !== 'instrument');
}

export function drumSampleIdentity(sample = {}) {
  if (sample?.sourceType === 'stock' && sample?.assetId) {
    return `stock:${sample.assetId}:v${sample.assetVersion || 1}`;
  }
  if (sample?.storageKey) return `storage:${normalizeStorageKey(sample.storageKey)}`;
  if (sample?.url) return `url:${sample.url}`;
  return null;
}

export function drumSampleBufferKey(sample) {
  const identity = drumSampleIdentity(sample);
  return identity ? `sample:${identity}` : null;
}

export function createPersistedDrumSample({
  existing = {}, name, url = null, storageKey = null,
  sourceType = null, assetId = null, assetVersion = 1
} = {}) {
  if (sourceType === 'stock' && assetId) {
    return {
      name: name || existing.name || 'Stock sample', loaded: true,
      sourceType: 'stock', assetId, assetVersion: assetVersion || 1
    };
  }
  const hasNewIdentity = Boolean(url || storageKey);
  const resolvedUrl = hasNewIdentity ? url : existing.url;
  const resolvedStorageKey = normalizeStorageKey(hasNewIdentity ? storageKey : existing.storageKey);
  return {
    name: name || existing.name || 'Sample', loaded: true,
    ...(sourceType === 'user' && assetId ? { sourceType: 'user', assetId, assetVersion: assetVersion || 1 } : {}),
    ...(resolvedUrl ? { url: resolvedUrl } : {}),
    ...(resolvedStorageKey ? { storageKey: resolvedStorageKey } : {})
  };
}

/**
 * Decide what a cached-buffer hydration actually has to change.
 *
 * Selecting a pattern clip re-hydrates every sample the clip references, and
 * those buffers are normally already decoded. Rescheduling cancels every
 * pattern voice the look-ahead scheduler has queued, so it must happen only
 * when the lane's audio genuinely changed; doing it on a no-op re-hydration is
 * heard as a stutter, once per lane.
 */
export function planCachedDrumSampleAssignment({
  laneBuffer = null, cachedBuffer = null, existingSample = null, nextSample = null
} = {}) {
  const bufferChanged = laneBuffer !== cachedBuffer;
  const fields = ['name', 'loaded', 'sourceType', 'assetId', 'assetVersion', 'url', 'storageKey'];
  const metadataChanged = drumSampleIdentity(existingSample) !== drumSampleIdentity(nextSample)
    || fields.some((field) => (existingSample?.[field] ?? null) !== (nextSample?.[field] ?? null));
  return { bufferChanged, metadataChanged, shouldReschedule: bufferChanged };
}

export function resolveDrumSampleBuffer(buffers = {}, clipData = {}, drumName) {
  const identityKey = drumSampleBufferKey(clipData?.customSamples?.[drumName]);
  // An explicit assignment must never play another clip's previous lane sound.
  return identityKey ? buffers[identityKey] || null : buffers[drumName] || null;
}

export function applyDrumSampleToClipData(clipData = {}, drumKey, sampleEntry, displayName) {
  return {
    ...clipData,
    customSamples: {
      ...(clipData.customSamples || {}),
      [drumKey]: { ...sampleEntry }
    },
    drumSounds: (clipData.drumSounds || []).map((drum) => (
      drum.key === drumKey ? { ...drum, name: displayName } : drum
    ))
  };
}
